import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  SensorDescriptor,
  SensorOptions,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Menu,
  MenuItem,
  MenuList,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import DeleteRounded from '@material-ui/icons/DeleteRounded';
import ExpandMore from '@material-ui/icons/ExpandMore';
import React, { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';

import { ErrorIcon, SuccessIcon } from '@/images/data-browser/icons';
import { useCurrentProjectModelInfoQuery } from '@/serverStore/projectModels';
import { useGetSelectedProjectQuery } from '@/serverStore/projects';
import {
  AnnotationChangeType,
  AnnotationSourceType,
  Dropdown,
  generateAnnotationId,
  IconButton,
  MediaInteractiveCanvas,
  MediaViewerSegmentationAnnotation,
  useKeyPress,
} from '@clef/client-library';
import {
  AnnotationType,
  AnnotationWithoutId,
  BoundingBoxAnnotationData,
  Dimensions,
  LabelingType,
  LabelSource,
  LabelType,
  MediaDetails,
  MediaDetailsWithPrediction,
  MediaLevelLabel,
  MediaStatusType,
} from '@clef/shared/types';
import cx from 'classnames';
import { isEmpty, uniqBy } from 'lodash';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';
import LabelAPI from '../../api/label_api';
import CLEF_PATH from '../../constants/path';
import useGetDefectColorById from '../../hooks/defect/useGetDefectColorById';
import DefectColor from '../../pages/LabelingTask/components/DefectColor';
import { useDefectSelector, useDefectSelectorWithArchived } from '../../store/defectState/actions';
import { getClassifiedClass, getDefectColor } from '../../utils';
import { useCurrentMediaStates } from './imageLabelingContext';
import {
  BitMapLabelingAnnotation,
  BoxLabelingAnnotation,
  ClassificationLabelingAnnotation,
  PureCanvasLabelingAnnotation,
  useLabelingState,
} from './labelingState';
import { useLabelingStyles } from './labelingStyles';
import { bitMapAnnotationToCanvasAnnotation } from './utils';
import { ClientFeatures, useFeatureGateEnabled } from '@/hooks/useFeatureGate';
import useGetDefectNameById from '@/hooks/defect/useGetDefectNameById';

type TransformScalerProps = {
  onMouseOver?: () => void;
  onMouseOut?: () => void;
};

const TransformScaler: React.FC<Dimensions & TransformScalerProps> = ({
  width,
  height,
  children,
  onMouseOut,
  onMouseOver,
}) => {
  const styles = useLabelingStyles();
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);

  const stageScale = containerNode
    ? Math.min(containerNode.clientWidth / width, containerNode.clientHeight / height)
    : 1;
  const stageStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: `translate(-50%, -50%) scale(${stageScale})`,
    width,
    height,
  };
  return (
    <div
      onMouseOver={() => {
        onMouseOver && onMouseOver();
      }}
      onMouseOut={() => {
        onMouseOut && onMouseOut();
      }}
      ref={node => {
        if (node) {
          setContainerNode(node);
        }
      }}
      className={styles.previewSegContainer}
      data-testid="preview-segmentation-container"
      style={{ paddingTop: `${100 / (width / height)}%` }}
    >
      <div style={stageStyle}>{children}</div>
    </div>
  );
};

export interface LabelPreviewListProps {
  mediaCanvasRef: MutableRefObject<MediaInteractiveCanvas | null>;
  labelingType?: LabelingType;
  annotations?: (
    | BitMapLabelingAnnotation
    | BoxLabelingAnnotation
    | ClassificationLabelingAnnotation
  )[];
  mediaDimensions?: Dimensions;
  isLabelMode?: boolean;
  isPrediction?: boolean;
  annotationSourceType?: AnnotationSourceType;
  defaultExpanded?: boolean;
  onAnnotationsChange?: (annotations: ClassificationLabelingAnnotation[]) => void;
  mediaDetails?: MediaDetails | MediaDetailsWithPrediction;
  isGroundTruth?: boolean;
  className?: string;
  mediaLevelLabel?: MediaLevelLabel;
}

interface BitMapAnnotationTreeItemProps extends LabelPreviewListProps {
  annotations?: BitMapLabelingAnnotation[] | PureCanvasLabelingAnnotation[];
}

export const BitMapLabelPreviewList: React.FC<BitMapAnnotationTreeItemProps> = ({
  mediaCanvasRef,
  annotations,
  mediaDimensions,
  isLabelMode,
  defaultExpanded,
  isPrediction,
  annotationSourceType,
  mediaLevelLabel,
}) => {
  const styles = useLabelingStyles();
  const theme = useTheme();
  const allDefectsWithArchived = useDefectSelectorWithArchived();
  const allDefectsWithoutArchived = useDefectSelector();
  const allDefects = isPrediction ? allDefectsWithArchived : allDefectsWithoutArchived;
  const mediaWidth = mediaDimensions?.width ?? 0;
  const mediaHeight = mediaDimensions?.height ?? 0;

  const getDefectColorById = useGetDefectColorById();

  if (!annotations || !annotations.length) {
    return mediaLevelLabel === MediaLevelLabel.OK ? (
      <Typography variant="body1" component="span">
        {isPrediction ? t('Predicted as No Class') : t('Marked as No Class')}
      </Typography>
    ) : (
      <Typography variant="body1" component="span">
        {isPrediction
          ? annotations
            ? t('Nothing predicted')
            : t('Unpredicted')
          : t('No labels yet')}
      </Typography>
    );
  }

  return (
    <>
      {annotations.map(ann => {
        const { defectId, data } = ann;
        // TransformScaler below lays this out in media coordinates, so the box has to be in media
        // coordinates too - a pure canvas may be cropped to the annotation, in which case its own
        // width and height say nothing about where it belongs
        const rangeBox =
          'rangeBox' in data ? data.rangeBox : (ann as PureCanvasLabelingAnnotation).rangeBox;
        const annotationData = 'bitMap' in data ? data.bitMap : data;

        const foundDefect = allDefects?.find(defect => defect.id === defectId);

        if (!foundDefect) {
          return null;
        }

        return (
          <Accordion
            square
            key={foundDefect.id}
            className={styles.previewContainer}
            role="listitem"
            title={`${foundDefect.name} preview`}
            defaultExpanded={defaultExpanded}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              className={!defaultExpanded ? styles.showExpandIconOnHover : undefined}
            >
              <Grid
                container
                alignItems="center"
                className={styles.previewContainerTop}
                onMouseOver={() => {
                  mediaCanvasRef.current?.setHoveredAnnotation({
                    color: getDefectColorById(ann.defectId),
                    group: annotationSourceType,
                  });
                }}
                onMouseOut={() => mediaCanvasRef.current?.setHoveredAnnotation()}
              >
                <DefectColor color={getDefectColor(foundDefect)} />
                <Typography variant="body2" component="div">
                  {foundDefect.name}
                </Typography>
                <div className={styles.flexGrow} />
                {isLabelMode && (
                  <IconButton
                    id="task-delete-defect"
                    size="small"
                    className={styles.deleteAction}
                    color="secondary"
                    aria-label="delete label"
                    onClick={() => {
                      mediaCanvasRef.current?.setAnnotations(
                        // We will replace existing defect instance with erased instance
                        // this way we can ensure orders are correct and overwrites still work
                        // e.g. if a pixel is red, overwrites with purple, delete purple => result should be empty
                        annotations.map(bitMapAnn =>
                          bitMapAnnotationToCanvasAnnotation(
                            bitMapAnn as BitMapLabelingAnnotation,
                            defectId => {
                              if (defectId === foundDefect.id) {
                                return 'transparent';
                              } else {
                                const getDefect = allDefects!.find(
                                  defect => defect.id === defectId,
                                )!;
                                return getDefectColor(getDefect);
                              }
                            },
                            AnnotationSourceType.GroundTruth,
                          ),
                        ),
                        AnnotationChangeType.DeleteColor,
                      );
                    }}
                  >
                    <DeleteRounded fontSize="small" htmlColor={theme.palette.secondary.main} />
                  </IconButton>
                )}
              </Grid>
            </AccordionSummary>
            <TransformScaler
              width={mediaWidth}
              height={mediaHeight}
              onMouseOver={() => {
                mediaCanvasRef.current?.setHoveredAnnotation({
                  color: getDefectColorById(ann.defectId),
                  group: annotationSourceType,
                });
              }}
              onMouseOut={() => mediaCanvasRef.current?.setHoveredAnnotation()}
            >
              <MediaViewerSegmentationAnnotation
                color={getDefectColor(foundDefect)}
                xMin={rangeBox.xmin}
                xMax={rangeBox.xmax}
                yMin={rangeBox.ymin}
                yMax={rangeBox.ymax}
                compressedBitMap={annotationData}
                compress
              />
            </TransformScaler>
          </Accordion>
        );
      })}
    </>
  );
};

interface BoxLabelPreviewListProps extends LabelPreviewListProps {
  annotations?: BoxLabelingAnnotation[];
  classes?: { labelPreviewContainer?: string };
}

const BoundingBoxLabelPreviewItem: React.FC<{
  annotation: BoxLabelingAnnotation;
  mediaCanvasRef: MutableRefObject<MediaInteractiveCanvas | null>;
  index: number;
  annotationSourceType?: AnnotationSourceType;
  showDefect?: boolean;
}> = ({ annotation, mediaCanvasRef, index, annotationSourceType, showDefect = false }) => {
  const styles = useLabelingStyles();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: annotation.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'pointer',
    zIndex: isDragging ? 1 : 0,
  };

  const getDefectColorById = useGetDefectColorById();
  const getDefectNameById = useGetDefectNameById();

  const defects = useDefectSelector();

  const updateAnnotationClass = (annotationId: string, defectId: number) => {
    const defectColor = getDefectColorById(defectId);
    mediaCanvasRef.current?.setAnnotations(prev => {
      const newAnnotations = prev.map(ann => {
        if (ann.id !== annotationId) return ann;
        return {
          ...ann,
          defectId,
          data: { ...ann.data, color: defectColor },
        };
      });
      return newAnnotations;
    }, AnnotationChangeType.Edit);
  };

  const [showClassNameTooltip, setShowClassNameTooltip] = useState(false);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      aria-selected={annotation.selected}
      className={cx(
        styles.labelPreviewItemText,
        showDefect && styles.bboxPreviewItemNoPadding,
        annotation.selected && 'selected',
      )}
      onClick={() => {
        if (annotation.selected) {
          mediaCanvasRef.current?.setSelectedAnnotation('');
        } else {
          mediaCanvasRef.current?.setSelectedAnnotation(annotation.id);
        }
      }}
      onMouseOver={e => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        mediaCanvasRef.current?.setHoveredAnnotation({
          annotationId: annotation.id,
          group: annotationSourceType,
        });
      }}
      onMouseOut={e => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        mediaCanvasRef.current?.setHoveredAnnotation();
      }}
    >
      {showDefect && (
        <Dropdown
          dropdown={toggleOpen => (
            <MenuList>
              {defects.map(d => (
                <MenuItem
                  key={d.id}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    updateAnnotationClass(annotation.id, d.id);
                    toggleOpen(false);
                  }}
                >
                  <DefectColor color={getDefectColorById(d.id)} />
                  {d.name}
                </MenuItem>
              ))}
            </MenuList>
          )}
          placement="bottom"
        >
          <Tooltip
            title={getDefectNameById(annotation.defectId)}
            placement="left"
            arrow
            open={showClassNameTooltip}
          >
            <Box
              onMouseEnter={() => setShowClassNameTooltip(true)}
              onMouseLeave={() => setShowClassNameTooltip(false)}
            >
              <DefectColor color={getDefectColorById(annotation.defectId)} />
            </Box>
          </Tooltip>
        </Dropdown>
      )}
      {t('{{index}} - x {{x}}, y {{y}}, {{width}} x {{height}}', {
        index: index + 1,
        x: Math.round(annotation.data.xmin),
        y: Math.round(annotation.data.ymin),
        width: Math.round(annotation.data.xmax - annotation.data.xmin),
        height: Math.round(annotation.data.ymax - annotation.data.ymin),
      })}
      <div style={{ flex: 1 }} />
      <span className={styles.labelPreviewItemConfidence}>
        {annotation.confidence ? `${annotation.confidence.toFixed(2)}` : ''}
      </span>
    </div>
  );
};

export const BoundingBoxLabelFlattenPreviewList: React.FC<
  BoxLabelPreviewListProps & {
    sensors: SensorDescriptor<SensorOptions>[];
  }
> = ({ mediaCanvasRef, annotations, annotationSourceType, sensors, isPrediction }) => {
  if (!annotations) {
    return null;
  }

  return (
    <Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={event => {
          const { active, over } = event;

          if (active.id !== over?.id) {
            mediaCanvasRef.current?.setAnnotations(prev => {
              const oldIndex = prev.findIndex(prev => prev.id === active.id);
              const newIndex = prev.findIndex(prev => prev.id === over?.id);

              return arrayMove(prev, oldIndex, newIndex).map(ann => ({
                ...ann,
                // We need to modify the id to created some strange "caching" behavior inside canvas
                id: generateAnnotationId(),
              }));
            }, AnnotationChangeType.Edit);
          }
        }}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={annotations.map(ann => ann.id)}
          strategy={verticalListSortingStrategy}
          disabled={isPrediction}
        >
          {annotations.map((ann, index) => (
            <BoundingBoxLabelPreviewItem
              key={ann.id}
              annotation={ann}
              mediaCanvasRef={mediaCanvasRef}
              index={index}
              annotationSourceType={annotationSourceType}
              showDefect
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export const BoundingBoxLabelPreviewList: React.FC<BoxLabelPreviewListProps> = props => {
  const {
    mediaCanvasRef,
    annotations,
    defaultExpanded,
    isPrediction,
    mediaLevelLabel,
    annotationSourceType,
    classes,
  } = props;
  const styles = useLabelingStyles();
  const allDefectsWithArchived = useDefectSelectorWithArchived();
  const allDefectsWithoutArchived = useDefectSelector();
  const allDefects = isPrediction ? allDefectsWithArchived : allDefectsWithoutArchived;
  const [showAllClasses, setShowAllLabels] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Ensure items can still respond to click event. See https://github.com/clauderic/dnd-kit/issues/591#issuecomment-1017050816
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const showBoxOrdering = useFeatureGateEnabled(ClientFeatures.ObjectDetectionSupportOrdering);

  if (isEmpty(allDefects) || isEmpty(annotations)) {
    return mediaLevelLabel === MediaLevelLabel.OK ? (
      <Typography variant="body1" component="span">
        {isPrediction ? t('Predicted as No Class') : t('Marked as No Class')}
      </Typography>
    ) : (
      <Typography variant="body1" component="span">
        {isPrediction
          ? annotations
            ? t('Nothing predicted')
            : t('Unpredicted')
          : t('No labels yet')}
      </Typography>
    );
  }

  if (showBoxOrdering) {
    return <BoundingBoxLabelFlattenPreviewList {...props} sensors={sensors} />;
  }

  const classesWithAnnotations = allDefects
    .map(defect => {
      const annotationsForThisDefect = annotations!.filter(ann => ann.defectId === defect.id);
      return annotationsForThisDefect.length ? defect : null;
    })
    .filter(Boolean) as typeof allDefects;

  const displayClasses = showAllClasses
    ? classesWithAnnotations
    : classesWithAnnotations.slice(0, 2);

  return (
    <>
      {displayClasses.map(defect => {
        const annotationsForThisDefect = annotations!.filter(ann => ann.defectId === defect.id);
        const annotationsForThisDefectUniq = uniqBy(annotationsForThisDefect, ann => ann.id);
        return annotationsForThisDefect.length ? (
          <Accordion
            square
            key={defect.id}
            className={cx(styles.labelPreviewContainer, classes?.labelPreviewContainer)}
            defaultExpanded={defaultExpanded}
            data-testid={`${defect.name}-label-preview`}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              className={!defaultExpanded ? styles.showExpandIconOnHover : undefined}
              data-testid="label-preview-defect-name"
              onMouseOver={() => {
                mediaCanvasRef.current?.setHoveredAnnotation({
                  annotationId: annotationsForThisDefect.map(ann => ann.id),
                  group: annotationSourceType,
                });
              }}
              onMouseOut={() => {
                mediaCanvasRef.current?.setHoveredAnnotation();
              }}
            >
              <Grid container alignItems="center" justifyContent="space-between">
                <Box className={styles.defectInfo}>
                  <DefectColor color={getDefectColor(defect)} />
                  <Tooltip title={`${defect.name}`} placement="top" arrow>
                    <Box className={cx(styles.defectNameBox, styles.defectNameBoxMaxWidthOverride)}>
                      {`${defect.name}`}
                    </Box>
                  </Tooltip>
                </Box>
                <Box>{annotationsForThisDefect.length}</Box>
              </Grid>
            </AccordionSummary>
            <AccordionDetails className={styles.labelPreviewItem}>
              <Box width="100%">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={event => {
                    const { active, over } = event;

                    if (active.id !== over?.id) {
                      mediaCanvasRef.current?.setAnnotations(prev => {
                        const oldIndex = prev.findIndex(prev => prev.id === active.id);
                        const newIndex = prev.findIndex(prev => prev.id === over?.id);

                        return arrayMove(prev, oldIndex, newIndex).map(ann => ({
                          ...ann,
                          // We need to modify the id to created some strange "caching" behavior inside canvas
                          id: generateAnnotationId(),
                        }));
                      }, AnnotationChangeType.Edit);
                    }
                  }}
                  modifiers={[restrictToVerticalAxis]}
                >
                  <SortableContext
                    items={annotationsForThisDefectUniq.map(ann => ann.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {annotationsForThisDefectUniq.map((ann, index) => (
                      <BoundingBoxLabelPreviewItem
                        key={ann.id}
                        annotation={ann}
                        mediaCanvasRef={mediaCanvasRef}
                        index={index}
                        annotationSourceType={annotationSourceType}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </Box>
            </AccordionDetails>
          </Accordion>
        ) : null;
      })}
      {classesWithAnnotations.length > 2 && (
        <>
          {showAllClasses ? (
            <Box
              className={styles.moreClasses}
              onClick={() => {
                setShowAllLabels(false);
              }}
            >
              {t('Show less')}
            </Box>
          ) : (
            <Box
              className={styles.moreClasses}
              onClick={() => {
                setShowAllLabels(true);
              }}
            >
              {t(`Show {{extraCount}} more class{{plural}}`, {
                extraCount: classesWithAnnotations.length - 2,
                plural: classesWithAnnotations.length - 2 > 1 ? 'es' : '',
              })}
            </Box>
          )}
        </>
      )}
    </>
  );
};

type ClassificationLabelPreviewListProps = Omit<LabelPreviewListProps, 'mediaCanvasRef'> & {
  enableColor: boolean;
};

export const ClassificationLabelPreviewList: React.FC<ClassificationLabelPreviewListProps> = ({
  annotations = [],
  onAnnotationsChange,
  enableColor,
  isPrediction,
  isGroundTruth,
}) => {
  const styles = useLabelingStyles();
  const { id: currentModelId } = useCurrentProjectModelInfoQuery();
  const allDefectsWithArchived = useDefectSelectorWithArchived();
  const allDefectsWithoutArchived = useDefectSelector();
  const allDefects = isPrediction ? allDefectsWithArchived : allDefectsWithoutArchived;
  // assume there is only one annotation
  const annotation = annotations[0];
  const defect = allDefects.find(defect => defect.id === annotation?.defectId);
  const defectColor = defect ? getDefectColor(defect) : 'white';
  const history = useHistory();

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'classification-label-preview',
  });

  const { enqueueSnackbar } = useSnackbar();
  const { data: project } = useGetSelectedProjectQuery();
  const { id: projectId, labelType } = project ?? {};
  const { mediaDetails } = useCurrentMediaStates();

  const classifiedClass = useMemo(() => {
    return getClassifiedClass(mediaDetails?.predictionLabel?.annotations || [], allDefects!);
  }, [allDefects, mediaDetails?.predictionLabel?.annotations]);

  const gtClassifiedClass = useMemo(() => {
    return getClassifiedClass(mediaDetails?.label?.annotations || [], allDefects!);
  }, [allDefects, mediaDetails?.label?.annotations]);

  const statusIndicator = useMemo((): {
    el: React.ReactNode;
    type: 'question' | 'success' | 'error';
  } | null => {
    if (labelType !== LabelType.Classification) return null;
    if (!isPrediction) return null;
    if (!classifiedClass?.id) return null;

    if (!gtClassifiedClass?.id) {
      return {
        el: <div className={styles.question}>?</div>,
        type: 'question',
      };
    }
    if (classifiedClass?.id === gtClassifiedClass?.id) {
      return {
        el: <SuccessIcon />,
        type: 'success',
      };
    }
    return {
      el: <ErrorIcon />,
      type: 'error',
    };
  }, [classifiedClass?.id, gtClassifiedClass?.id, isPrediction, labelType, styles.question]);

  const handleChangeClass = useCallback(
    async (defectId: number) => {
      const defect = allDefects.find(defect => defect.id === defectId);
      onAnnotationsChange?.([
        {
          id: Date.now().toString(32),
          defectId,
          data: {},
        },
      ]);
      const serverAnnotation = {
        defectId,
        annotationType: AnnotationType.classification,
        dataSchemaVersion: 3,
      } as AnnotationWithoutId;
      popupState.close();
      if (labelType === LabelType.Classification && projectId) {
        await LabelAPI.upsertLabels({
          projectId,
          mediaId: mediaDetails!.id,
          annotations: [serverAnnotation],
          mediaLevelLabel: MediaLevelLabel.NG,
          source: LabelSource.DirectLabeling,
          modelId: currentModelId,
        });
        enqueueSnackbar(t(`Successfully set class to {{className}}`, { className: defect?.name }), {
          variant: 'success',
        });
      }
    },
    [
      allDefects,
      enqueueSnackbar,
      labelType,
      mediaDetails,
      onAnnotationsChange,
      popupState,
      projectId,
      currentModelId,
    ],
  );
  const anchorRef = useRef<HTMLDivElement>(null);
  useKeyPress('d', () => {
    popupState.setOpen(!popupState.isOpen, anchorRef.current || undefined);
  });

  const gotoDefect = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      history.push(CLEF_PATH.data.defectBookEnhanced);
    },
    [history],
  );

  const mediaStatus = mediaDetails?.mediaStatus;
  const isInTask = mediaStatus === MediaStatusType.InTask;

  const score = useMemo(() => {
    if (labelType === LabelType.Classification && isPrediction) {
      return mediaDetails?.predictionLabel?.mediaLevelScore?.toFixed(2) || '';
    }
    return '';
  }, [isPrediction, labelType, mediaDetails?.predictionLabel?.mediaLevelScore]);

  return (
    <div>
      {/* TODO: merge these with ClassChip when design is ready for data browser */}
      {onAnnotationsChange ? (
        <>
          {allDefects.length > 0 && (
            <Menu {...bindPopover(popupState)} classes={{ paper: styles.changeClassPopover }}>
              {allDefects.map(defect => (
                <MenuItem
                  key={defect.id}
                  onClick={() => handleChangeClass(defect.id)}
                  style={enableColor ? { color: getDefectColor(defect) } : undefined}
                >
                  {defect.name}
                </MenuItem>
              ))}
            </Menu>
          )}
          <Tooltip
            arrow
            interactive
            title={
              !allDefects.length
                ? t('No defect was found. Please create one in the {{link}}.', {
                    link: (
                      <a className={styles.toDefect} onClick={gotoDefect}>
                        {t('defect book')}
                      </a>
                    ),
                  })
                : isInTask && isGroundTruth
                ? t('Image is in a labeling task.')
                : ''
            }
          >
            <Box>
              <div
                className={styles.classificationPreviewBadge}
                data-testid="set-class"
                style={enableColor ? { borderColor: defectColor } : undefined}
                {...bindTrigger(popupState)}
                ref={anchorRef}
              >
                <Typography variant="body2" component="div" className={styles.classificationName}>
                  {defect?.name ?? t('Select Class')}
                </Typography>
                <Box ml={1} />
                <ExpandMore fontSize="small" />
              </div>
            </Box>
          </Tooltip>
        </>
      ) : (
        <>
          {isPrediction ? (
            <Box
              display="inline-block"
              className={cx(
                styles.classificationPreviewBadge,
                defect?.name ? 'prediction' : 'no-prediction',
              )}
            >
              {isPrediction && statusIndicator && statusIndicator.el}
              <Typography
                variant="body2"
                component="div"
                className={styles.classificationName}
                style={{
                  marginLeft: isPrediction && statusIndicator ? 8 : 0,
                  marginRight: score !== '' ? 8 : 0,
                }}
              >
                {defect?.name ?? t('Not predicted')}
              </Typography>
              <Typography>{score}</Typography>
            </Box>
          ) : isInTask ? (
            <Box
              display="inline-block"
              className={cx(
                styles.classificationPreviewBadge,
                defect?.name ? 'prediction' : 'no-prediction',
              )}
              onClick={e => {
                if (isGroundTruth) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
            >
              <Typography variant="body2" component="div" className={styles.classificationName}>
                {t('In Task')}
              </Typography>
            </Box>
          ) : (
            <Box
              display="inline-block"
              className={cx(styles.classificationPreviewBadge, 'view-only')}
            >
              <Typography variant="body2" component="div" className={styles.classificationName}>
                {defect?.name ?? t('No label')}
              </Typography>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

const LabelPreviewList: React.FC<LabelPreviewListProps> = ({
  mediaCanvasRef,
  labelingType: labelingTypeProps,
  annotations,
  mediaDimensions,
  isLabelMode,
  isPrediction,
  annotationSourceType = AnnotationSourceType.GroundTruth,
  defaultExpanded,
  onAnnotationsChange,
  isGroundTruth,
  mediaLevelLabel,
}) => {
  const {
    state: { labelingType: labelingTypeState },
  } = useLabelingState();
  const { labelType: projectLabelType } = useGetSelectedProjectQuery().data ?? {};
  const labelingType = labelingTypeProps ?? labelingTypeState;

  return (
    <>
      {labelingType === LabelingType.DefectSegmentation && (
        <BitMapLabelPreviewList
          mediaCanvasRef={mediaCanvasRef}
          annotations={
            annotations?.filter(
              ann => 'bitMap' in ann.data || ann.data instanceof OffscreenCanvas,
            ) as BitMapLabelingAnnotation[]
          }
          mediaDimensions={mediaDimensions}
          isLabelMode={isLabelMode}
          defaultExpanded={defaultExpanded}
          isPrediction={isPrediction}
          annotationSourceType={annotationSourceType}
          mediaLevelLabel={mediaLevelLabel}
        />
      )}
      {labelingType === LabelingType.DefectBoundingBox && (
        <BoundingBoxLabelPreviewList
          mediaCanvasRef={mediaCanvasRef}
          annotations={
            annotations?.filter(
              ann => typeof (ann.data as BoundingBoxAnnotationData).xmin !== 'undefined',
            ) as BoxLabelingAnnotation[]
          }
          defaultExpanded={defaultExpanded}
          isPrediction={isPrediction}
          annotationSourceType={annotationSourceType}
          mediaLevelLabel={mediaLevelLabel}
        />
      )}
      {labelingType === LabelingType.DefectClassification && (
        <ClassificationLabelPreviewList
          annotations={annotations}
          onAnnotationsChange={onAnnotationsChange}
          enableColor={projectLabelType === LabelType.AnomalyDetection}
          isPrediction={isPrediction}
          isGroundTruth={isGroundTruth}
        />
      )}
    </>
  );
};

export default LabelPreviewList;
